<template>
  <div class="space-y-5">
    <!-- Score summary + change over time (bar/boxplot; hidden for line charts) -->
    <div
      v-if="showBarChartInsights"
      class="flex flex-col sm:flex-row gap-4"
    >
      <div class="flex-1 space-y-5 text-base sm:text-lg bg-white rounded-lg shadow-sm p-4 sm:p-5">
      <div class="flex items-baseline justify-between mb-3">
        <div class="flex items-center gap-2">
          <i class="pi pi-chart-bar text-orange-500 text-lg sm:text-xl"></i>
          <h4 class="text-base sm:text-lg font-semibold text-gray-800">
            {{ $t('ResultsInsightsPanel.scoreSummary') }}
          </h4>
        </div>
        <div class="flex items-baseline gap-2 rounded-lg">
          <span
            class="text-2xl sm:text-3xl font-bold text-gray-900"
          >{{ formatOwnResultNumber(overallAverage) }}</span>
          <span class="text-sm sm:text-base text-gray-400">/5.00</span>
        </div>
      </div>
      <!-- Mini bars per dimension -->
      <div class="flex flex-col gap-2 justify-between">
        <div
          v-for="dim in dimensionEntries"
          :key="dim.key"
          class="flex items-center gap-2"
        >
          <span
            class="text-sm sm:text-base text-gray-600 truncate flex-1"
            :title="dim.label"
          >
            {{ dim.label }}
          </span>
          <span class="text-sm sm:text-base font-medium text-gray-700 w-10 text-right">
            {{ dim.score.toFixed(2) }}
          </span>
        </div>
      </div>
    </div>
  
    <!-- Change Over Time Card (only when previous assessment exists) -->
    <div
      v-if="dimensionChanges && dimensionChanges.length > 0"
      class="flex-1 space-y-5 text-base sm:text-lg bg-white rounded-lg shadow-sm p-4 sm:p-5"
    >
      <div class="flex items-center gap-2 mb-3">
        <i class="pi pi-arrow-right-arrow-left text-orange-500"></i>
        <h4 class="text-base sm:text-lg font-semibold text-gray-800">
          {{ $t('ResultsInsightsPanel.changeOverTime') }}
        </h4>
      </div>
      <p class="text-sm sm:text-base text-gray-500 mb-3">
        {{ $t('ResultsInsightsPanel.comparedTo', { date: previousDate }) }}
      </p>
      <div class="space-y-3">
        <div
          v-for="change in dimensionChanges"
          :key="change.key"
          class="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1.15fr)] sm:items-start sm:gap-x-3"
        >
          <span
            class="text-sm sm:text-base text-gray-600 truncate min-w-0"
            :title="change.label"
          >
            {{ change.label }}
          </span>
          <div class="flex items-center gap-1.5 shrink-0">
            <span
              class="text-sm sm:text-base font-semibold px-1.5 py-0.5 rounded bg-gray-50"
              :class="changeClass(change)"
            >
              {{ formatAbsoluteChange(change.absoluteChange) }}
            </span>
            <span
              class="text-sm sm:text-base whitespace-nowrap"
              :class="changeTextClass(change)"
            >
              <i
                :class="changeIconClass(change)"
                class="text-xs"
              ></i>
              {{ formatPercentChange(change.percentChange) }}
            </span>
          </div>
          <p
            class="text-xs sm:text-sm leading-snug min-w-0"
            :class="changeTextClass(change)"
          >
            {{ $t(absoluteChangeInterpretationI18nKey(change.absoluteChange)) }}
          </p>
        </div>
      </div>
      <!-- Overall interpretation (vs. previous assessment counts) -->
      <div
        v-if="changeSummary && changeOverallInterpretationI18nKey"
        class="mt-3 pt-3 border-t border-gray-100 space-y-2"
      >
        <p class="text-xs sm:text-sm font-semibold text-gray-700">
          {{ $t('ResultsInsightsPanel.changeOverallInterpretHeading') }}
        </p>
        <p
          class="text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line"
        >
          {{ $t(changeOverallInterpretationI18nKey) }}
        </p>
      </div>
    </div>
    </div>

    <!-- Strengths & Growth Card -->
    <slot name="descriptions" v-if="showBarChartInsights">
      <div
        v-if="descriptions && descriptions.length > 0"
        class="space-y-5 text-base sm:text-lg bg-white rounded-lg shadow-sm p-4 sm:p-5"
      >
        <div class="flex items-center gap-2 mb-3">
          <i class="pi pi-star text-orange-500"></i>
          <h4 class="text-base sm:text-lg font-semibold text-gray-800">
            {{ $t('ResultsInsightsPanel.strengthsAndGrowth') }}
          </h4>
        </div>
        <!-- Strengths -->
        <div class="space-y-2 mb-3">
          <h5 class="text-sm sm:text-base font-medium text-gray-800">
            {{ $t('MyResultsDashboardComponent.yourStrongestDimensions') }}
          </h5>
          <div
            v-for="desc in positiveDescriptions"
            :key="`pos-${desc.rank}`"
            class="flex items-start gap-2 rounded-md p-2"
          >
            <i class="pi pi-check-circle text-[var(--color-orange)] mt-0.5 text-sm"></i>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 text-gray-800 ">
                <span class="text-base sm:text-lg font-medium truncate">
                  {{ desc.headline || desc.dimensionName }}
                </span>
                <span
                  v-if="getScoreForDimension(desc.dimensionName)"
                  class="text-sm sm:text-base font-semibold px-1.5 py-0.5 rounded"
                >
                  {{ getScoreForDimension(desc.dimensionName) }}/5.00
                </span>
              </div>
              <p
                v-if="desc.content"
                class="text-sm sm:text-base text-gray-600 mt-0.5 line-clamp-2"
              >
                {{ desc.content }}
              </p>
              <Divider />
              <p
                v-if="desc.tip"
                class="text-sm sm:text-base text-gray-600 mt-1 rounded px-1.5 py-1"
              >
                {{ $t('MyResultsDashboardComponent.developmentTip') }}: 
              </p>
              <p v-if="desc.tip" class="text-sm sm:text-base text-gray-600 mt-1 rounded px-1.5 py-1">{{ desc.tip }}</p>
            </div>
          </div>
        </div>
        <!-- Growth areas -->
        <div class="space-y-2">
          <h5 class="text-sm sm:text-base font-medium text-gray-800">
            {{ $t('MyResultsDashboardComponent.youCanImproveIn') }}
          </h5>
          <div
            v-for="desc in negativeDescriptions"
            :key="`neg-${desc.rank}`"
            class="flex items-start gap-2 rounded-md p-2"
          >
            <i class="pi pi-arrow-up-right text-[var(--color-orange)] mt-0.5 text-sm"></i>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 text-gray-800">
                <span class="text-base sm:text-lg font-medium truncate">
                  {{ desc.headline || desc.dimensionName }}
                </span>
                <span
                  v-if="getScoreForDimension(desc.dimensionName)"
                  class="text-sm sm:text-base font-semibold px-1.5 py-0.5 rounded"
                >
                  {{ getScoreForDimension(desc.dimensionName) }}/5.00
                </span>
              </div>
              <p
                v-if="desc.content"
                class="text-sm sm:text-base text-gray-600 mt-0.5 line-clamp-2"
              >
                {{ desc.content }}
              </p>
              <Divider />
              <p
                v-if="desc.tip"
                class="text-sm sm:text-base text-gray-600 mt-1 rounded px-1.5 py-1"
              >
                {{ $t('MyResultsDashboardComponent.developmentTip') }}: 
              </p>
              <p v-if="desc.tip" class="text-sm sm:text-base text-gray-600 mt-1 rounded px-1.5 py-1">{{ desc.tip }}</p>
              
            </div>
          </div>
        </div>
      </div>
    </slot>

    <!-- Statistical Context Card -->
    <div
      v-if="showBarChartInsights && statisticalContext"
      class="space-y-5 text-base sm:text-lg bg-white rounded-lg shadow-sm p-4 sm:p-5"
    >
      <div class="flex items-center gap-2 mb-2">
        <i class="pi pi-info-circle text-orange-500 text-lg sm:text-xl"></i>
        <h4 class="text-base sm:text-lg font-semibold text-gray-800">
          {{ $t('ResultsInsightsPanel.statisticalContext') }}
        </h4>
      </div>
      <p class="text-sm sm:text-base text-gray-600 leading-relaxed">
        {{ $t(`ResultsInsightsPanel.${statisticalContext.messageKey}`, {
          sd: statisticalContext.averageSD
        }) }}
      </p>
    </div>

    <!-- Extra slot for tenant-specific content -->
    <slot name="extra" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatOwnResultNumber } from '@/helpers/formatOwnResultNumber';
import { Divider } from 'primevue';

const props = defineProps({
  /** Overall average score (number) */
  overallAverage: { type: Number, default: 0 },
  /** Array of { key, label, score } sorted desc */
  dimensionEntries: { type: Array, default: () => [] },
  /** Array of dimension change objects, or null */
  dimensionChanges: { type: Array, default: null },
  /** { improved, declined, unchanged, total } or null */
  changeSummary: { type: Object, default: null },
  /** Date string of the previous assessment */
  previousDate: { type: String, default: '' },
  /** Personalized descriptions array from parent */
  descriptions: { type: Array, default: () => [] },
  /** { averageSD, variability, messageKey } */
  statisticalContext: { type: Object, default: null },
  /** Current dimension scores map { key: score } */
  currentScores: { type: Object, default: () => ({}) },
  /**
   * When false, hides score summary, change over time, and statistical context
   * (used for line charts; strengths/growth and slots stay available).
   */
  showBarChartInsights: { type: Boolean, default: true },
});

const averageBarColor = computed(() => {
  if (props.overallAverage >= 4) return 'bg-green-500';
  if (props.overallAverage >= 3) return 'bg-orange-400';
  return 'bg-red-400';
});

const positiveDescriptions = computed(
  () => props.descriptions.filter((d) => d.type === 'positive'),
);

const negativeDescriptions = computed(
  () => props.descriptions.filter((d) => d.type === 'negative'),
);

/**
 * Aggregate story from improved vs declined dimension counts (ties → mixed).
 */
const changeOverallInterpretationI18nKey = computed(() => {
  const s = props.changeSummary;
  if (!s) return '';
  const { improved, declined } = s;
  if (declined > improved) return 'ResultsInsightsPanel.changeOverallInterpretDecline';
  if (improved > declined) return 'ResultsInsightsPanel.changeOverallInterpretImprove';
  return 'ResultsInsightsPanel.changeOverallInterpretMixed';
});

function getScoreForDimension(dimensionName) {
  // Try to find score by dimension name in entries
  const entry = props.dimensionEntries.find(
    (e) => e.label === dimensionName || e.key === dimensionName,
  );
  return entry ? entry.score.toFixed(2) : null;
}

/** Change-over-time font colors: positive / no change / negative */
function changeTextClass(change) {
  if (change.improved) return 'text-[#e4821a]';
  if (change.unchanged) return 'text-[#475569]';
  return 'text-[#e44b55]';
}

function changeIconClass(change) {
  if (change.improved) return 'pi pi-arrow-up';
  if (change.unchanged) return 'pi pi-minus';
  return 'pi pi-arrow-down';
}

function changeClass(change) {
  if (change.improved) return 'text-[#e4821a]';
  if (change.unchanged) return 'text-[#475569]';
  return 'text-[#e44b55]';
}

function formatAbsoluteChange(value) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}`;
}

function formatPercentChange(value) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Band labels from absolute score delta (current − previous), matching product copy.
 * Treats |delta| < 0.1 as stable; +0.5 and −0.5 go to significant / slight respectively.
 */
function absoluteChangeInterpretationI18nKey(x) {
  if (x == null || Number.isNaN(x)) return 'ResultsInsightsPanel.changeInterpretationStable';
  if (Math.abs(x) < 0.1) return 'ResultsInsightsPanel.changeInterpretationStable';
  if (x > 0) {
    if (x < 0.5) return 'ResultsInsightsPanel.changeInterpretationSlightImprovement';
    if (x <= 1.0) return 'ResultsInsightsPanel.changeInterpretationSignificantImprovement';
    return 'ResultsInsightsPanel.changeInterpretationStrongImprovement';
  }
  if (x >= -0.5) return 'ResultsInsightsPanel.changeInterpretationSlightDecline';
  if (x >= -1.0) return 'ResultsInsightsPanel.changeInterpretationSignificantDecline';
  return 'ResultsInsightsPanel.changeInterpretationSharpDecline';
}
</script>
